/**
 * ImportClientsModalV2.jsx
 *
 * ENHANCEMENT: 2025-12-07 - Enhanced Import Clients Modal
 * Created for: Improved validation and error handling for bulk client import
 *
 * Features:
 * - Row-level validation with format checking (PAN, GSTIN, TAN, DOB)
 * - Empty cell detection
 * - Error table display with row/column details
 * - Download Error Rows functionality
 * - Upload Valid Rows Only option
 * - Auto-skip "Error Remarks" column on re-upload
 *
 * Fallback: Falls back to original ImportClientsModal on critical error
 */

import React, { useState } from "react";
import { Modal, Button, Upload, Alert, Table, Tag, Tooltip } from "antd";
import {
  InboxOutlined,
  WarningOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { FaDownload } from "react-icons/fa";
import {
  validateImportData,
  generateErrorRemarks,
  ERROR_REMARKS_COLUMN,
} from "../utils/importValidation";

const { Dragger } = Upload;

// User-friendly column names that users can use in their Excel files.
// USER_LEVEL_CLIENT_ASSIGNMENT.md §6 (BE Phase 8): each portal template
// now carries an OPTIONAL trailing "Assigned User Email" column. The BE
// import service maps blank → NULL (Shared), valid org-user email → FK,
// and unknown / cross-org email → row error. Old 4-column uploads still
// work — see ASSIGNED_USER_EMAIL_COLUMN handling in the validator.
const ASSIGNED_USER_EMAIL_COLUMN = "Assigned User Email";

const TEMPLATES = {
  "income-tax": ["Name Of Assessee", "PAN/TAN", "Password", "DOB", ASSIGNED_USER_EMAIL_COLUMN],
  tds: ["Client Name", "Username", "Password", "TAN Number", ASSIGNED_USER_EMAIL_COLUMN],
  gstin: ["Client Name", "GSTIN", "Username", "Password", ASSIGNED_USER_EMAIL_COLUMN],
};

// Mapping from user-friendly names to Frappe field names (for upload)
const FIELD_NAME_MAPPING = {
  "income-tax": {
    "Name Of Assessee": "client_name",
    "PAN/TAN": "username",
    "Password": "password",
    "DOB": "dob",
    [ASSIGNED_USER_EMAIL_COLUMN]: "assigned_user_email",
  },
  tds: {
    "Client Name": "client_name",
    "Username": "tds_username",
    "Password": "tds_password",
    "TAN Number": "tan_number",
    [ASSIGNED_USER_EMAIL_COLUMN]: "assigned_user_email",
  },
  gstin: {
    "Client Name": "client_name",
    "GSTIN": "gstin",
    "Username": "gst_username",
    "Password": "gst_password",
    [ASSIGNED_USER_EMAIL_COLUMN]: "assigned_user_email",
  },
};

const ImportClientsModalV2 = ({
  importClientModal,
  closeImportClientModal,
  serviceType,
  handleUpload,
  uploading,
  importStatus,
  downloadTemplate,
  renderImportStatusDetails,
}) => {
  const [fileList, setFileList] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // New states for enhanced validation
  const [validationResult, setValidationResult] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [headers, setHeaders] = useState([]);

  const getExpectedColumns = () => {
    return TEMPLATES[serviceType] || [];
  };

  /**
   * Validates column structure including Error Remarks skip logic.
   * USER_LEVEL_CLIENT_ASSIGNMENT.md §6: also accepts uploads that are
   * MISSING the trailing "Assigned User Email" column — old templates
   * predate the BE Phase 8 change. When missing we treat each row's
   * assigned_user_email as blank → BE persists NULL (= Shared).
   */
  const validateColumns = (uploadedHeaders, expectedHeaders) => {
    // Check if last column is "Error Remarks" - auto-skip it
    let headersToValidate = [...uploadedHeaders];
    let hasErrorRemarksColumn = false;

    if (uploadedHeaders[uploadedHeaders.length - 1] === ERROR_REMARKS_COLUMN) {
      headersToValidate = uploadedHeaders.slice(0, -1);
      hasErrorRemarksColumn = true;
    }

    // Backwards-compat: an old template (without Assigned User Email)
    // is exactly 1 column shorter than expected. Pad expectedHeaders
    // by trimming the trailing assignment column for the equality check
    // — but preserve the original expectedHeaders for the error message
    // so users still see the FULL recommended template.
    const expectedHeadersForCheck =
      headersToValidate.length === expectedHeaders.length - 1 &&
      expectedHeaders[expectedHeaders.length - 1] === ASSIGNED_USER_EMAIL_COLUMN
        ? expectedHeaders.slice(0, -1)
        : expectedHeaders;

    // Column count check
    if (headersToValidate.length !== expectedHeadersForCheck.length) {
      const extraColumns = headersToValidate.filter(
        (col) => !expectedHeaders.includes(col)
      );
      const missingColumns = expectedHeaders.filter(
        (col) => !headersToValidate.includes(col)
      );

      let errorMsg = `Column count mismatch. Expected ${expectedHeaders.length} columns, but found ${headersToValidate.length}.`;

      if (missingColumns.length > 0) {
        errorMsg += `\n\nMissing columns: ${missingColumns.join(", ")}`;
      }

      if (extraColumns.length > 0) {
        errorMsg += `\n\nUnexpected columns: ${extraColumns.join(", ")}`;
      }

      errorMsg += `\n\nExpected columns: ${expectedHeaders.join(", ")}`;

      return { isValid: false, error: errorMsg, hasErrorRemarksColumn };
    }

    // Column name and sequence check (against the trimmed expected list
    // when the upload lacks Assigned User Email — see backwards-compat
    // branch above).
    const columnsMatch = expectedHeadersForCheck.every(
      (col, index) => headersToValidate[index] === col
    );

    if (!columnsMatch) {
      const differences = expectedHeadersForCheck
        .map((expected, index) => {
          const uploaded = headersToValidate[index];
          if (expected !== uploaded) {
            return `Column ${index + 1}: Expected "${expected}", got "${uploaded}"`;
          }
          return null;
        })
        .filter(Boolean);

      return {
        isValid: false,
        error: `Column mismatch detected:\n\n${differences.join(
          "\n"
        )}\n\nPlease use the exact template with columns in this order:\n${expectedHeaders.join(
          ", "
        )}`,
        hasErrorRemarksColumn,
      };
    }

    return { isValid: true, error: null, hasErrorRemarksColumn };
  };

  /**
   * Main file validation function with row-level validation
   */
  const validateFile = async (file) => {
    // xlsx (~800 KB) loads on demand when a file is actually imported
    // (2026-08-19 bundle diet).
    const XLSX = await import("xlsx");
    setIsValidating(true);
    setValidationError(null);
    setValidationResult(null);
    setParsedData(null);
    setHeaders([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Empty file check
      if (jsonData.length === 0) {
        setValidationError(
          "The uploaded file is empty. Please add data to the template."
        );
        setIsValidating(false);
        return false;
      }

      // Extract and clean headers
      const uploadedHeaders = jsonData[0]
        .filter(
          (header) => header !== undefined && header !== null && header !== ""
        )
        .map((header) => String(header).trim());

      const expectedHeaders = getExpectedColumns();

      // Validate column structure
      const columnValidation = validateColumns(uploadedHeaders, expectedHeaders);

      if (!columnValidation.isValid) {
        setValidationError(columnValidation.error);
        setIsValidating(false);
        return false;
      }

      // No data rows check
      if (jsonData.length < 2) {
        setValidationError(
          "No data rows found. Please add at least one client record to import."
        );
        setIsValidating(false);
        return false;
      }

      // Extract data rows (exclude Error Remarks column if present)
      let dataRows = jsonData.slice(1);
      if (columnValidation.hasErrorRemarksColumn) {
        // Remove last column (Error Remarks) from each row
        dataRows = dataRows.map((row) => row.slice(0, expectedHeaders.length));
      }

      // Filter out completely empty rows
      dataRows = dataRows.filter((row) =>
        row.some(
          (cell) =>
            cell !== undefined && cell !== null && String(cell).trim() !== ""
        )
      );

      if (dataRows.length === 0) {
        setValidationError(
          "No valid data rows found. All rows appear to be empty."
        );
        setIsValidating(false);
        return false;
      }

      // Row-level validation
      const result = validateImportData(dataRows, serviceType, expectedHeaders);

      setHeaders(expectedHeaders);
      setParsedData(dataRows);
      setValidationResult(result);
      setIsValidating(false);

      // Return true if at least some rows are valid
      return result.validRows.length > 0;
    } catch (error) {
      console.error("Validation error:", error);
      setValidationError(
        "Unable to process the file. Please ensure it is a valid Excel file (.xlsx or .xls) and follows the template format."
      );
      setIsValidating(false);
      return false;
    }
  };

  /**
   * Download error rows as Excel with Error Remarks column
   */
  const downloadErrorRows = async () => {
    const XLSX = await import("xlsx");
    if (!validationResult || validationResult.invalidRows.length === 0) return;

    const expectedHeaders = getExpectedColumns();
    const errorHeaders = [...expectedHeaders, ERROR_REMARKS_COLUMN];

    const errorData = validationResult.invalidRows.map((row) => {
      const rowData = [...row.data];
      // Pad row data if needed
      while (rowData.length < expectedHeaders.length) {
        rowData.push("");
      }
      // Add error remarks
      rowData.push(generateErrorRemarks(row.errors));
      return rowData;
    });

    const wsData = [errorHeaders, ...errorData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-size columns
    const colWidths = errorHeaders.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...errorData.map((row) => String(row[i] || "").length)
      );
      return { wch: Math.min(maxLen + 2, 50) };
    });
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Error Rows");

    const filename = `${getServiceTitle()}_Import_Errors_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  /**
   * Convert user-friendly headers to Frappe field names
   */
  const convertToFrappeFieldNames = (userHeaders) => {
    const mapping = FIELD_NAME_MAPPING[serviceType] || {};
    return userHeaders.map((header) => mapping[header] || header);
  };

  /**
   * Handle upload of valid rows only
   */
  const handleUploadValidOnly = async () => {
    if (!validationResult || validationResult.validRows.length === 0) return;
    const XLSX = await import("xlsx");

    // Create a new workbook with only valid rows
    const expectedHeaders = getExpectedColumns();
    // Convert to Frappe field names for upload
    const frappeHeaders = convertToFrappeFieldNames(expectedHeaders);
    const validData = validationResult.validRows.map((row) => row.data);

    const wsData = [frappeHeaders, ...validData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Valid Clients");

    // Convert to blob and create file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const validFile = new File([blob], "valid_clients.xlsx", {
      type: blob.type,
    });

    handleUpload(validFile);
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList: fileList,
    accept: ".xlsx,.xls",
    beforeUpload: async (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");

      if (!isExcel) {
        setValidationError("You can only upload Excel files (.xlsx or .xls)!");
        return Upload.LIST_IGNORE;
      }

      const hasValidRows = await validateFile(file);

      if (hasValidRows) {
        setFileList([file]);
      } else {
        setFileList([]);
      }

      return false;
    },
    onRemove: () => {
      setFileList([]);
      setValidationError(null);
      setValidationResult(null);
      setParsedData(null);
      setHeaders([]);
    },
  };

  const getServiceTitle = () => {
    switch (serviceType) {
      case "gstin":
        return "GST";
      case "tds":
        return "TDS";
      default:
        return "Income Tax";
    }
  };

  const resetStates = () => {
    setFileList([]);
    setValidationError(null);
    setValidationResult(null);
    setParsedData(null);
    setHeaders([]);
    setIsValidating(false);
  };

  /**
   * Render validation summary
   */
  const renderValidationSummary = () => {
    if (!validationResult) return null;

    const { summary, validRows, invalidRows } = validationResult;
    const allValid = invalidRows.length === 0;
    const allInvalid = validRows.length === 0;

    return (
      <div className="mb-4 w-full text-left">
        {allValid ? (
          <Alert
            message="All Rows Valid"
            description={`All ${summary.total} rows passed validation. Click 'Upload All' to proceed.`}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : allInvalid ? (
          <Alert
            message="All Rows Invalid"
            description={`All ${summary.total} rows have validation errors. Please download the error file, fix the issues, and re-upload.`}
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
          />
        ) : (
          <Alert
            message="Partial Validation"
            description={
              <div>
                <p>
                  <Tag color="green">{summary.valid} valid</Tag>
                  <Tag color="red">{summary.invalid} invalid</Tag>
                  out of {summary.total} total rows
                </p>
                <p className="mt-2 text-gray-600">
                  You can upload only valid rows now, or download error rows to fix and re-upload later.
                </p>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />
        )}
      </div>
    );
  };

  /**
   * Render error table for invalid rows
   */
  const renderErrorTable = () => {
    if (!validationResult || validationResult.invalidRows.length === 0)
      return null;

    const columns = [
      {
        title: "Row",
        dataIndex: "rowNumber",
        key: "rowNumber",
        width: 60,
        fixed: "left",
      },
      ...headers.map((header, index) => ({
        title: header,
        dataIndex: `col_${index}`,
        key: `col_${index}`,
        width: 120,
        ellipsis: true,
        render: (text, record) => {
          const hasError = record.errors.some((e) => e.column === header);
          // §7.1 / §6: blank Assigned User Email cells render as "Shared"
          // — the BE persists NULL which means "no one assigned"; calling
          // it out explicitly is friendlier than the generic "(empty)"
          // placeholder used for genuinely-missing required fields.
          const isAssignmentColumn = header === ASSIGNED_USER_EMAIL_COLUMN;
          const placeholder = isAssignmentColumn ? (
            <span className="text-gray-500 italic">Shared</span>
          ) : (
            <span className="text-gray-400">(empty)</span>
          );
          return (
            <Tooltip title={hasError ? record.errors.find((e) => e.column === header)?.error : null}>
              <span style={{ color: hasError ? "#ff4d4f" : "inherit" }}>
                {text || placeholder}
              </span>
            </Tooltip>
          );
        },
      })),
      {
        title: "Errors",
        dataIndex: "errorRemarks",
        key: "errorRemarks",
        width: 250,
        render: (text) => (
          <span className="text-red-500 text-xs">{text}</span>
        ),
      },
    ];

    const dataSource = validationResult.invalidRows.map((row, index) => {
      const record = {
        key: index,
        rowNumber: row.rowNumber,
        errors: row.errors,
        errorRemarks: generateErrorRemarks(row.errors),
      };
      headers.forEach((header, colIndex) => {
        record[`col_${colIndex}`] = row.data[colIndex];
      });
      return record;
    });

    return (
      <div className="mb-4 w-full">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium text-red-600">
            Invalid Rows ({validationResult.invalidRows.length})
          </span>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadErrorRows}
          >
            Download Error Rows
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={dataSource}
          size="small"
          scroll={{ x: "max-content", y: 150 }}
          pagination={false}
          className="border rounded"
        />
      </div>
    );
  };

  /**
   * Render action buttons based on validation result
   */
  const renderActionButtons = () => {
    if (!validationResult) return null;

    const { validRows, invalidRows } = validationResult;
    const allValid = invalidRows.length === 0;
    const hasValidRows = validRows.length > 0;

    if (allValid) {
      return (
        <Button
          key="upload-all"
          type="primary"
          onClick={() => handleUpload(fileList[0])}
          loading={uploading}
          disabled={importStatus === "processing"}
          icon={<CloudUploadOutlined />}
        >
          Upload All ({validRows.length} rows)
        </Button>
      );
    }

    if (hasValidRows) {
      return (
        <Button
          key="upload-valid"
          type="primary"
          onClick={handleUploadValidOnly}
          loading={uploading}
          disabled={importStatus === "processing"}
          icon={<CloudUploadOutlined />}
        >
          Upload Valid Rows Only ({validRows.length} rows)
        </Button>
      );
    }

    return null;
  };

  return (
    <Modal
      title={`Import ${getServiceTitle()} Clients`}
      open={importClientModal}
      onCancel={() => {
        closeImportClientModal();
        resetStates();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            closeImportClientModal();
            resetStates();
          }}
        >
          {importStatus === "completed" ? "Close" : "Cancel"}
        </Button>,
        importStatus !== "failed" &&
          importStatus !== "completed" &&
          renderActionButtons(),
      ]}
      width={1000}
      centered
    >
      <div className="py-4 flex flex-col text-center max-h-[500px] overflow-y-auto">
        {importStatus === "processing" ? (
          <div className="mb-6 w-full">
            <Alert
              message="Import in Progress"
              description="Please wait, we are importing clients for you. This may take a few minutes."
              type="info"
              showIcon
              icon={<SyncOutlined spin />}
            />
          </div>
        ) : importStatus === "waiting" ? (
          <>
            {!validationResult && (
              <>
                <div className="mb-6 w-full">
                  <p>
                    Please download our standardized Excel template below if you
                    haven't already. After populating the template with your client
                    information, upload the completed file to import your clients.
                  </p>
                </div>

                <div className="mb-6">
                  <Button
                    icon={<FaDownload />}
                    type="primary"
                    ghost
                    onClick={downloadTemplate}
                    className="mb-4"
                  >
                    Download Excel Template
                  </Button>
                </div>

                <div className="flex items-center justify-center w-full mb-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="mx-4 font-medium text-gray-500">OR</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
              </>
            )}

            {/* Validation Status Messages */}
            {isValidating && (
              <div className="mb-4 w-full text-left">
                <Alert
                  message="Validating File..."
                  description="Analyzing file structure, validating data formats, and checking for errors. This will only take a moment."
                  type="info"
                  showIcon
                  icon={<SyncOutlined spin />}
                />
              </div>
            )}

            {validationError && (
              <div className="mb-4 w-full text-left">
                <Alert
                  message="Validation Failed"
                  description={
                    <div
                      style={{
                        whiteSpace: "pre-line",
                        fontFamily: "monospace",
                        fontSize: "12px",
                      }}
                    >
                      {validationError}
                    </div>
                  }
                  type="error"
                  showIcon
                  icon={<CloseCircleOutlined />}
                  closable
                  onClose={() => setValidationError(null)}
                />
              </div>
            )}

            {/* Validation Result Summary */}
            {renderValidationSummary()}

            {/* Error Table */}
            {renderErrorTable()}

            {/* File Upload Area */}
            {!validationResult && (
              <div className="w-full">
                <Dragger
                  {...uploadProps}
                  disabled={importStatus === "processing" || isValidating}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">
                    Support for Excel files only. Ensure your file follows the
                    template format.
                  </p>
                  <p className="ant-upload-hint text-xs mt-2 text-gray-500">
                    Required columns: {getExpectedColumns().join(", ")}
                  </p>
                  <p className="ant-upload-hint text-xs mt-1 text-gray-500">
                    Tip: "Assigned User Email" can hold multiple emails
                    separated by a semicolon or comma to assign a client to
                    several team members. Leave it blank for Shared.
                  </p>
                </Dragger>
              </div>
            )}

            {/* Re-upload option after validation */}
            {validationResult && (
              <div className="mt-4 w-full text-center">
                <Button
                  type="link"
                  onClick={resetStates}
                >
                  Upload a different file
                </Button>
              </div>
            )}
          </>
        ) : (
          <></>
        )}

        {renderImportStatusDetails()}
      </div>
    </Modal>
  );
};

export default ImportClientsModalV2;
